import { useState, useEffect } from 'react';
import { SystemAdminPanel } from './SystemAdminPanel';
import { QuestionLibrary } from './QuestionLibrary';
import { projectId } from '../utils/supabase/info';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface SystemAdminRouterProps {
  accessToken: string;
  currentUser: any;
  onLogout: () => void;
}

export function SystemAdminRouter({ accessToken, currentUser, onLogout }: SystemAdminRouterProps) {
  console.log('[LOGOUT_TRACE] SystemAdminRouter received onLogout prop:', typeof onLogout);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Listen for navigation changes
  useEffect(() => {
    const handleLocationChange = () => {
      console.log('[SystemAdminRouter] Location changed to:', window.location.pathname);
      setCurrentPath(window.location.pathname);
    };

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleLocationChange);
    
    // Also listen for custom navigation event (for pushState)
    window.addEventListener('pathkit-navigate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('pathkit-navigate', handleLocationChange);
    };
  }, []);

  // PROTECTION: System Admin views should ONLY be accessible on root domain
  // If we're on a subdomain, the user should not see System Admin panel
  const hostname = window.location.hostname;
  const isRootDomain = hostname === 'pathkit.pro' || 
                       hostname === 'www.pathkit.pro' ||
                       hostname === 'staging.pathkit.pro' || 
                       hostname === 'localhost' || 
                       hostname.startsWith('localhost:') ||
                       hostname.includes('figmaiframepreview');
  const isSubdomain = !isRootDomain && (hostname.includes('.pathkit.pro') || hostname.endsWith('.pathkit.pro'));
  
  // Check both role and activeRole to handle demo users who switched roles
  const currentRole = currentUser?.activeRole || currentUser?.role;
  
  // If user is viewing as systemAdmin but we're on a subdomain, show error
  if (isSubdomain && currentRole === 'systemAdmin') {
    console.error('[SystemAdminRouter] ERROR: System Admin on subdomain detected!', {
      hostname,
      userRole: currentUser?.role,
      userActiveRole: currentUser?.activeRole,
      currentRole,
      userEmail: currentUser?.email,
      isDemo: currentUser?.isDemo,
    });
    
    // For demo users, show helpful redirect message
    if (currentUser?.isDemo === true) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="text-center max-w-2xl">
            <div className="mb-4"><RefreshCw className="h-16 w-16 text-blue-500 mx-auto animate-spin" /></div>
            <h1 className="text-3xl mb-4">Redirecting to System Admin...</h1>
            <p className="text-gray-600 mb-6">
              System Admin access is only available on the root domain. You'll be redirected automatically.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
              <h2 className="text-lg font-semibold text-blue-900 mb-3">Auto-Redirect</h2>
              <p className="text-blue-800 mb-4">
                You'll be redirected to <strong>pathkit.pro/system-admin</strong> in a moment...
              </p>
              <div className="flex items-center justify-center gap-4 py-4">
                <div className="text-sm text-blue-700">
                  <strong>Current Domain:</strong> {hostname}<br />
                  <strong>Target Domain:</strong> pathkit.pro
                </div>
              </div>
              
              {/* Manual redirect button */}
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => {
                    window.location.href = 'https://pathkit.pro/system-admin';
                  }}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
                >
                  Go to System Admin Now
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // For non-demo users, show the error and force logout
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="mb-4"><AlertTriangle className="h-16 w-16 text-amber-500 mx-auto" /></div>
          <h1 className="text-3xl mb-4">Role Configuration Error</h1>
          <p className="text-gray-600 mb-6">
            Your account is configured as a System Admin, but you're accessing a subdomain. 
            System Admin access is only available on the root domain (pathkit.pro).
          </p>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              <strong>Your account:</strong> {currentUser?.email}<br />
              <strong>Current domain:</strong> {hostname}<br />
              <strong>Assigned role:</strong> {currentUser?.role}
            </p>
            <div className="border-t pt-4">
              <p className="text-sm text-gray-600 mb-3">
                Please contact your administrator to fix your account role, or log out and log back in.
              </p>
              <button 
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = '/login';
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Clear Session & Re-Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Extract company ID from path like /company/:id
  const companyMatch = currentPath.match(/^\/company\/([a-zA-Z0-9_-]+)$/);
  console.log('[SystemAdminRouter] Current path:', currentPath);
  console.log('[SystemAdminRouter] Company match:', companyMatch);
  
  if (companyMatch) {
    // The company's own dashboard is retired (RESTORE_PLAN R3.6): Edit Company has its setup, domains and
    // JSM assessment, and opens over the panel.
    return <SystemAdminPanel accessToken={accessToken} currentUser={currentUser} onLogout={onLogout} initialCompanyId={companyMatch[1]} />;
  }

  if (currentPath === '/question-library') {
    console.log('[SystemAdminRouter] Rendering QuestionLibrary');
    return <QuestionLibrary />;
  }

  // Always render SystemAdminPanel for system admins (combines dashboard + admin panel)
  console.log('[SystemAdminRouter] Rendering SystemAdminPanel (default)');
  console.log('[LOGOUT_TRACE] SystemAdminRouter passing onLogout to SystemAdminPanel');
  return <SystemAdminPanel accessToken={accessToken} currentUser={currentUser} onLogout={onLogout} />;
}